import {view, Icon, tokens} from '@sail/ui';
import {check, cancel} from '@sail/icons/react/Icon';
import {Text} from 'src/v2/components/common/Text';
import './StepProgressIndicator.css';

export type StepStatus = 'completed' | 'inProgress' | 'pending' | 'failed';

type ProgressStep = {
  label: string;
  status: StepStatus;
};

type StepProgressIndicatorProps = {
  steps: ProgressStep[];
};

const CIRCLE_SIZE = '20px';

const StepCircle = ({status, index}: {status: StepStatus; index: number}) => {
  if (status === 'completed') {
    return (
      <view.div
        className="StepCircleCompleted"
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          backgroundColor: 'feedback.success',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          icon={check}
          css={{
            width: 'space.125',
            height: 'space.125',
            color: 'feedback.success',
          }}
        />
      </view.div>
    );
  }

  if (status === 'failed') {
    return (
      <view.div
        className="StepCircleFailed"
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          backgroundColor: 'feedback.critical',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon
          icon={cancel}
          css={{
            width: 'space.125',
            height: 'space.125',
            color: 'feedback.critical',
          }}
        />
      </view.div>
    );
  }

  if (status === 'inProgress') {
    const gradientId = `step_spinner_gradient_${index}`;
    return (
      <view.div
        css={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          backgroundColor: tokens.color.neutral[50].toString(),
          color: 'text',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="-0.75 -0.75 11.75 11.75"
          fill="none"
          className="StepSpinner"
        >
          <path
            d="M9.5 5.125C9.5 4.25971 9.24341 3.41385 8.76268 2.69438C8.28195 1.97492 7.59867 1.41416 6.79924 1.08303C5.99981 0.751894 5.12015 0.665254 4.27148 0.834064C3.42281 1.00287 2.64326 1.41955 2.03141 2.03141C1.41955 2.64326 1.00287 3.42281 0.834064 4.27148C0.665254 5.12015 0.751894 5.99981 1.08303 6.79924C1.41416 7.59867 1.97492 8.28195 2.69438 8.76268C3.41385 9.24341 4.25971 9.5 5.125 9.5"
            stroke={`url(#${gradientId})`}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient
              id={gradientId}
              x1="9.5"
              y1="2.91975"
              x2="3.07041"
              y2="11.0104"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="currentColor" />
              <stop
                stopColor="currentColor"
                offset="0.427083"
                stopOpacity="0.5573"
              />
              <stop stopColor="currentColor" offset="1" stopOpacity="0.3" />
            </linearGradient>
          </defs>
        </svg>
      </view.div>
    );
  }

  return (
    <view.div
      css={{
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        borderRadius: '50%',
        backgroundColor: tokens.color.neutral[50].toString(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Text type="captionEmphasized">{index + 1}</Text>
    </view.div>
  );
};

export const StepProgressIndicator = ({steps}: StepProgressIndicatorProps) => {
  return (
    <view.div css={{display: 'flex', flexDirection: 'column', gap: 'space.0'}}>
      {steps.map((step, i) => (
        <view.div
          key={step.label}
          css={{stack: 'x', gap: 'space.150', alignY: 'top'}}
        >
          <view.div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'space.0',
              alignItems: 'center',
              width: CIRCLE_SIZE,
              flexShrink: 0,
            }}
          >
            <StepCircle status={step.status} index={i} />
            {i < steps.length - 1 ? (
              <view.div
                css={{
                  width: 'space.1',
                  flex: 1,
                  minHeight: 'large',
                  backgroundColor: tokens.color.neutral[50].toString(),
                }}
              />
            ) : null}
          </view.div>
          <view.div
            css={{display: 'flex', alignItems: 'center', height: CIRCLE_SIZE}}
          >
            <Text type="body" css={{color: 'text'}}>
              {step.label}
            </Text>
          </view.div>
        </view.div>
      ))}
    </view.div>
  );
};
